import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { useState } from 'react';

import {
  type IntegrationConnectionType,
  type IntegrationProviderType,
  useIntegrationConnectionHealthChecks,
} from '@/api/integration';
import DisconnectConnectionDialog from '@/app/integrations/[provider]/DisconnectConnectionDialog';
import InfoPopover from '@/components/InfoPopover';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/Tooltip';
import { Link, Text } from '@/components/typography';

import ConnectionOrg from './ConnectionOrg';
import HealthCheckDialog from './HealthCheckDialog';
import IntegrationConnectionListItemStatus from './IntegrationConnectionListItemStatus';

type Props = {
  provider: IntegrationProviderType;
  connection: IntegrationConnectionType;
  onDisconnect: (connection: IntegrationConnectionType) => void;
};

const IntegrationConnectionListItem = ({ provider, connection, onDisconnect }: Props) => {
  const [confirmDisconnect, setConfirmDisconnect] = useState<boolean>(false);
  const [showHealthCheckDialog, setShowHealthCheckDialog] = useState<boolean>(false);

  const { healthChecks, error: healthCheckCallError } = useIntegrationConnectionHealthChecks(
    connection.id,
  );

  const author = connection.createdBy;
  const orgName = connection?.organizationName;
  const orgId = connection?.organizationId;
  const hasHealthCheckFailures = healthChecks && healthChecks.length > 0;
  const isConnectionActive = connection.status === 'active' && !healthCheckCallError;
  const syncError = connection.lastJob?.errors?.trim();

  const handleDisconnect = async () => {
    onDisconnect(connection);
  };

  return (
    <div
      className="flex flex-col items-start gap-1"
      data-testid="integration-detail-connectionItem"
    >
      <Text variant="label" color="dark-grey">
        {author ? author.name : t`Unknown user`}
      </Text>
      <Tooltip disabled={!author?.email}>
        <TooltipTrigger
          render={
            <Text variant="body1" className="truncate">
              {author ? author.email : <Trans>Unknown email address</Trans>}
            </Text>
          }
        />
        <TooltipContent>{author?.email}</TooltipContent>
      </Tooltip>
      <ConnectionOrg orgId={orgId} orgName={orgName} />
      {hasHealthCheckFailures ? (
        <Text variant="footnote" color="red" weight="regular">
          <Trans>
            There is a problem with this account.{' '}
            <Link
              color="static-red"
              variant="footnote"
              weight="bold"
              onClick={() => setShowHealthCheckDialog(true)}
            >
              View details
            </Link>
          </Trans>
        </Text>
      ) : isConnectionActive ? (
        <IntegrationConnectionListItemStatus provider={provider} lastJob={connection.lastJob} />
      ) : (
        <Text
          variant="footnote"
          color="red"
          weight="regular"
          className="inline-flex items-center gap-1"
        >
          <Trans>There is a problem with this connection.</Trans>
          {syncError && <InfoPopover size="large" title={t`Error during sync`} body={syncError} />}
        </Text>
      )}
      <Link variant="body2" color="static-grey" onClick={() => setConfirmDisconnect(true)}>
        <Trans>Disconnect {provider.name}</Trans>
      </Link>
      <DisconnectConnectionDialog
        open={confirmDisconnect}
        provider={provider}
        connection={connection}
        onOk={handleDisconnect}
        onCancel={() => setConfirmDisconnect(false)}
      />
      {healthChecks && (
        <HealthCheckDialog
          provider={provider}
          healthChecks={healthChecks}
          open={showHealthCheckDialog}
          onClose={() => setShowHealthCheckDialog(false)}
        />
      )}
    </div>
  );
};

export default IntegrationConnectionListItem;
